import { FaCheck } from 'react-icons/fa';
import { css, Flex } from 'theme-ui';
import styled from '../../lib/transient-styled';
import pxToRem from '../../utils/pixelsToRem';
import { TextButton } from './buttons';

const inputBase = {
  width: '100%',
  mb: '40px',
  padding: '24px 24px 13px 24px',
  background: 'inputBackground',
  borderRadius: '3px',
  color: 'white',
  borderBottom: '1px solid',
  borderColor: 'inputBorder',

  '&:focus': {
    borderBottom: '2px solid',
    borderColor: 'focus',
    pb: '12px',
    outline: 'none',
  },

  // Autofill fix
  '&:-webkit-autofill, &:-webkit-autofill:focus': {
    transition: 'background-color 600000s 0s, color 600000s 0s',
  },

  '&[data-autocompleted]': {
    background: 'inputBackground !input',
  },
};

export const Form = styled.form(
  css({
    width: ['100%', '90%', '430px'],
  }),
);

export const FormField = styled.div(
  css({
    position: 'relative',

    '& input:not(.independent)': {
      ...inputBase,
      mb: '15px',
    },

    '& select': {
      ...inputBase,
      mb: '15px',
    },

    '& textarea': {
      ...inputBase,
      height: '208px',
      mb: '15px',
      resize: 'vertical',
      borderRadius: 0,
    },

    '& textarea + p': {
      mb: '2px',
    },

    '&:focus-within label, & label.filled': {
      transform: 'translate(0, 8px) scale(0.75)',
    },

    '& :is(input, textarea, select).filled': {
      borderBottom: '2px solid',
      pb: '12px',
      outline: 'none',
    },

    '&:has(textarea) label.filled': {
      display: 'none',
    },
  }),
  ({ $isErrored, $hideLabelAfterFocus }) =>
    css({
      '&:focus-within label, & label.filled': {
        color: $isErrored ? 'danger' : 'focus',
        display: $hideLabelAfterFocus && 'none',
      },
      '& :is(input:not(.independent), textarea, select).filled': {
        borderColor: $isErrored ? 'danger' : 'focus',
        pb: $isErrored && '24px',
      },
      '&& :is(input:not(.independent), textarea, select), & :is(input, textarea, select):focus':
        {
          borderColor: $isErrored && 'danger',
          pb: $isErrored && '24px',
        },
    }),
);

export const Label = styled.label(
  css({
    position: 'absolute',
    left: '24px',
    transform: 'translate(0, 20px) scale(1)',
    transformOrigin: 'top left',
    fontWeight: '400',
    fontSize: pxToRem(16),
    letterSpacing: '0.035em',
    color: 'inputText',
    pointerEvents: 'none',

    transition: '200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
  }),
);

export const SubmitButton = styled(TextButton)(
  css({
    marginTop: '24px',
    px: '34px',
    py: '18px',
  }),
);

export const HelpingButton = styled.button(
  css({
    fontSize: pxToRem(14),
    fontWeight: '500',
    lineHeight: pxToRem(24),
    letterSpacing: '0.035em',
    color: 'helpingText',
    backgroundColor: 'inherit',
    cursor: 'pointer',
  }),
);

export const ErrorText = styled.p(
  css({
    position: 'absolute',
    width: '100%',
    zIndex: '10',
    bottom: '20px',
    pl: '24px',
    textAlign: 'left',
    fontSize: pxToRem(12),
    color: 'danger',
  }),
);

export const SelectErrorText = styled.p(
  css({
    position: 'absolute',
    zIndex: '10',
    top: '46px',
    pl: '24px',
    textAlign: 'left',
    fontSize: pxToRem(12),
    color: 'danger',
  }),
);

export const ThanksText = styled.div(
  css({
    '&:not(.styled-content)': {
      marginTop: '30px',
      fontFamily: '"Urbanist"',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: pxToRem(40),
      lineHeight: pxToRem(40),
      color: 'focus',
    },

    '& div:first-child': {
      marginTop: '30px',
      fontFamily: '"Urbanist"',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: pxToRem(40),
      lineHeight: pxToRem(40),
      color: 'focus',
      overflow: 'visible',
    },
  }),
);

export const ThankYouBlock = styled(Flex)(({ $isSmall = false }) =>
  css({
    height: $isSmall ? '260px' : '100%',
    width: $isSmall ? '430px' : '470px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px 70px',
    background: 'rgba(5, 28, 33, 0.8)',
    border: '1px solid #414D50',
    backdropFilter: 'blur(2px)',
  }),
);

export const CheckMark = styled(FaCheck)(
  css({
    width: '37px',
    height: '37px',
    color: 'focus',
  }),
);

export const CheckboxesArray = styled('div')(
  css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '16px',
    marginTop: '16px',
  }),
);

export const CheckboxField = styled('div')(
  css({
    display: 'flex',
    gap: '8px',
  }),
);

export const AttachmentWrapper = styled.div(
  css({
    '& input[type="file"]': {
      paddingTop: '2.75rem',
    },
  }),
);
